<template>
  <div class="roulette-lab">
    <section class="lab-hero">
      <div class="lab-hero__copy">
        <span class="eyebrow">{{ $t('forge.roulette.eyebrow') }}</span>
        <h1>{{ $t('forge.roulette.title') }}</h1>
        <p>{{ $t('forge.roulette.intro') }}</p>
      </div>
      <div class="lab-hero__status">
        <span>{{ $t('forge.data.snapshot') }}</span>
        <strong>{{ metrics.total.toLocaleString() }}</strong>
        <small>{{ $t('forge.data.entries') }} · {{ metrics.generatedAt }}</small>
      </div>
    </section>

    <section class="roulette-console" aria-label="Loadout controls">
      <div class="side-switch" role="group" :aria-label="$t('forge.common.side')">
        <button
          type="button"
          :class="{ 'is-active': side === 'survivor' }"
          :aria-pressed="side === 'survivor'"
          @click="setSide('survivor')"
        >
          <span class="side-switch__emblem side-switch__emblem--survivor" aria-hidden="true"><i></i><i></i><i></i><i></i></span>
          <span><small>{{ $t('forge.common.playAs') }}</small>{{ $t('forge.common.survivor') }}</span>
        </button>
        <button
          type="button"
          :class="{ 'is-active': side === 'killer' }"
          :aria-pressed="side === 'killer'"
          @click="setSide('killer')"
        >
          <span class="side-switch__emblem side-switch__emblem--killer" aria-hidden="true"><i></i><i></i><i></i></span>
          <span><small>{{ $t('forge.common.playAs') }}</small>{{ $t('forge.common.killer') }}</span>
        </button>
      </div>

      <div class="chaos-module">
        <div class="chaos-module__header">
          <div>
            <span class="eyebrow">{{ $t('forge.chaos.label') }}</span>
            <strong>{{ chaosLabel }}</strong>
          </div>
          <output>{{ chaos }}%</output>
        </div>
        <input
          v-model.number="chaos"
          type="range"
          min="0"
          max="100"
          step="25"
          :aria-label="$t('forge.chaos.label')"
          @change="syncQuery"
        >
        <div class="chaos-module__ticks" aria-hidden="true">
          <span v-for="level in chaosLevels" :key="level">{{ level }}</span>
        </div>
        <p>{{ $t('forge.chaos.rule') }}</p>
      </div>

      <button type="button" class="roll-button" :disabled="rolling" @click="roll">
        <span class="roll-button__spark" aria-hidden="true"></span>
        <span>
          <small>{{ rolling ? $t('forge.roulette.calculating') : $t('forge.roulette.actionHint') }}</small>
          {{ rolling ? $t('forge.roulette.rolling') : $t('forge.roulette.roll') }}
        </span>
      </button>
    </section>

    <transition name="loadout-swap" mode="out-in">
      <section v-if="result" :key="side" :class="['loadout-shell', { 'is-rolling': rolling }]">
        <header class="loadout-shell__header">
          <div>
            <span class="eyebrow">{{ $t('forge.roulette.generated') }}</span>
            <h2>{{ profileLabel }}</h2>
            <div v-if="result.plan" class="build-plan-meta">
              <span>{{ $t('forge.roulette.wholeBuildPlan') }}</span>
              <strong v-if="planSecondaryLabel">{{ $t('forge.roulette.secondaryPlan') }} : {{ planSecondaryLabel }}</strong>
              <em>{{ planCoverageLabel }}</em>
            </div>
          </div>
          <div class="loadout-score">
            <span>{{ $t('forge.roulette.coherence') }}</span>
            <strong>{{ result.coherence }}</strong>
            <small>/100</small>
          </div>
        </header>

        <div class="loadout-grid">
          <article :class="['character-card', 'loadout-card', { 'is-cascade-active': isCascadeSlot('character') }]">
            <div class="slot-actions">
              <button type="button" class="slot-action slot-action--reroll" :disabled="rolling" :aria-label="rerollLabel('character')" :title="rerollLabel('character')" @click="rerollFrom('character')">
                <span class="reroll-icon" aria-hidden="true"></span>
              </button>
              <button type="button" :class="['slot-action', 'slot-action--lock', { 'is-locked': locks.character }]" :aria-label="lockLabel('character')" :title="lockLabel('character')" @click="toggleLock('character')">
                <span class="lock-icon" aria-hidden="true"></span>
              </button>
            </div>
            <div class="character-card__portrait" :style="{ '--profile-accent': result.profile.accent }">
              <img v-if="result.character.icon" :src="result.character.icon" :alt="characterName" decoding="async">
              <span v-else>{{ characterInitials }}</span>
              <small>{{ side === 'killer' ? $t('forge.common.killer') : $t('forge.common.survivor') }}</small>
            </div>
            <div class="character-card__copy">
              <span>{{ $t('forge.roulette.character') }}</span>
              <h3>{{ characterName }}</h3>
              <p v-if="characterRealName">{{ characterRealName }}</p>
              <strong v-if="side === 'killer'">{{ characterPower || $t('forge.roulette.uniquePower') }}</strong>
              <strong v-else>{{ $t('forge.roulette.chaosImmune') }}</strong>
            </div>
          </article>

          <div class="perk-section">
            <div class="section-label">
              <div>
                <span>{{ $t('forge.roulette.perks') }}</span>
                <small class="cascade-hint"><i></i>{{ $t('forge.roulette.cascadeHint') }}</small>
              </div>
              <small>{{ $t('forge.roulette.chaosAffected') }}</small>
            </div>
            <div class="perk-grid">
              <article
                v-for="(perk, index) in result.perks"
                :key="perk.id"
                :class="['perk-tile', 'loadout-card', 'loadout-card--inspectable', { 'is-cascade-active': isCascadeSlot('perk-' + index) }]"
                role="button"
                tabindex="0"
                :aria-label="infoLabel(perk, 'perk')"
                @click="openInfo(perk, 'perk', $event)"
                @keydown.enter.prevent="openInfo(perk, 'perk', $event)"
                @keydown.space.prevent="openInfo(perk, 'perk', $event)"
              >
                <span :class="['perk-tile__role', { 'perk-tile__role--central': index === 0 }]">{{ perkSlotLabel(index) }}</span>
                <div class="slot-actions">
                  <button type="button" class="slot-action slot-action--reroll" :disabled="rolling" :aria-label="rerollLabel('perk-' + index)" :title="rerollLabel('perk-' + index)" @click="rerollFrom('perk-' + index)">
                    <span class="reroll-icon" aria-hidden="true"></span>
                  </button>
                  <button type="button" :class="['slot-action', 'slot-action--lock', { 'is-locked': locks['perk-' + index] }]" :aria-label="lockLabel('perk-' + index)" :title="lockLabel('perk-' + index)" @click="toggleLock('perk-' + index)">
                    <span class="lock-icon" aria-hidden="true"></span>
                  </button>
                </div>
                <div class="perk-tile__visual">
                  <PerkIcon :perk="perk" :side="side" />
                  <span>{{ String(index + 1).padStart(2, '0') }}</span>
                </div>
                <h3>{{ perkName(perk) }}</h3>
                <p>{{ perkRole(perk) }}</p>
              </article>
            </div>
          </div>
        </div>

        <div class="equipment-row">
          <article
            v-if="side === 'survivor' && result.item"
            :class="['equipment-card', 'equipment-card--item', 'loadout-card', 'loadout-card--inspectable', rarityCardClass(result.item.rarity), { 'is-cascade-active': isCascadeSlot('item') }]"
            role="button"
            tabindex="0"
            :aria-label="infoLabel(result.item, 'item')"
            @click="openInfo(result.item, 'item', $event)"
            @keydown.enter.prevent="openInfo(result.item, 'item', $event)"
            @keydown.space.prevent="openInfo(result.item, 'item', $event)"
          >
            <div class="slot-actions">
              <button type="button" class="slot-action slot-action--reroll" :disabled="rolling" :aria-label="rerollLabel('item')" :title="rerollLabel('item')" @click="rerollFrom('item')">
                <span class="reroll-icon" aria-hidden="true"></span>
              </button>
              <button type="button" :class="['slot-action', 'slot-action--lock', { 'is-locked': locks.item }]" :aria-label="lockLabel('item')" :title="lockLabel('item')" @click="toggleLock('item')">
                <span class="lock-icon" aria-hidden="true"></span>
              </button>
            </div>
            <GameIcon :src="result.item.icon" :alt="entryName(result.item, 'item')" :rarity="result.item.rarity" kind="item" />
            <div class="equipment-card__copy"><span class="equipment-card__type">{{ $t('forge.roulette.item') }}</span><strong>{{ entryName(result.item, 'item') }}</strong><small>{{ categoryName(result.item) }} · {{ $t('forge.roulette.chaosImmune') }}</small><div v-if="result.item.correlation" class="fit-summary"><em class="fit-badge">{{ fitTier(result.item.correlation) }} · {{ result.item.correlation.score }}</em><small>{{ correlationReason(result.item.correlation) }}</small></div></div>
            <span :class="['rarity-dot', rarityClass(result.item.rarity)]">{{ rarityName(result.item.rarity) }}</span>
          </article>
          <article v-else :class="['equipment-card', 'loadout-card', 'equipment-card--power', 'rarity-card--power', { 'is-cascade-active': isCascadeSlot('character') }]">
            <GameIcon :src="result.character.powerIcon" :alt="characterPower || $t('forge.roulette.uniquePower')" :rarity="0" kind="power" />
            <div class="equipment-card__copy"><span class="equipment-card__type">{{ $t('forge.roulette.power') }}</span><strong>{{ characterPower || $t('forge.roulette.uniquePower') }}</strong><small>{{ $t('forge.roulette.chaosImmune') }}</small></div>
            <span class="rarity-dot rarity-dot--power">{{ $t('forge.roulette.killerBound') }}</span>
          </article>

          <article
            v-for="(addon, index) in result.addons"
            :key="addon.id"
            :class="['equipment-card', 'equipment-card--addon', 'loadout-card', 'loadout-card--inspectable', rarityCardClass(addon.rarity), { 'is-cascade-active': isCascadeSlot('addon-' + index) }]"
            role="button"
            tabindex="0"
            :aria-label="infoLabel(addon, 'addon')"
            @click="openInfo(addon, 'addon', $event)"
            @keydown.enter.prevent="openInfo(addon, 'addon', $event)"
            @keydown.space.prevent="openInfo(addon, 'addon', $event)"
          >
            <div class="slot-actions">
              <button type="button" class="slot-action slot-action--reroll" :disabled="rolling" :aria-label="rerollLabel('addon-' + index)" :title="rerollLabel('addon-' + index)" @click="rerollFrom('addon-' + index)">
                <span class="reroll-icon" aria-hidden="true"></span>
              </button>
              <button type="button" :class="['slot-action', 'slot-action--lock', { 'is-locked': locks['addon-' + index] }]" :aria-label="lockLabel('addon-' + index)" :title="lockLabel('addon-' + index)" @click="toggleLock('addon-' + index)">
                <span class="lock-icon" aria-hidden="true"></span>
              </button>
            </div>
            <GameIcon :src="addon.icon" :alt="entryName(addon, 'addon')" :rarity="addon.rarity" kind="addon" />
            <div class="equipment-card__copy"><span class="equipment-card__type">{{ $t('forge.roulette.addon') }} {{ String(index + 1).padStart(2, '0') }}</span><strong>{{ entryName(addon, 'addon') }}</strong><small>{{ $t('forge.roulette.chaosAffected') }}</small><div v-if="addon.correlation" class="fit-summary"><em class="fit-badge">{{ fitTier(addon.correlation) }} · {{ addon.correlation.score }}</em><small>{{ correlationReason(addon.correlation) }}</small></div></div>
            <span :class="['rarity-dot', rarityClass(addon.rarity)]">{{ rarityName(addon.rarity) }}</span>
          </article>

          <article
            v-if="result.offering"
            :class="['equipment-card', 'equipment-card--offering', 'loadout-card', 'loadout-card--inspectable', rarityCardClass(result.offering.rarity), { 'is-cascade-active': isCascadeSlot('offering') }]"
            role="button"
            tabindex="0"
            :aria-label="infoLabel(result.offering, 'offering')"
            @click="openInfo(result.offering, 'offering', $event)"
            @keydown.enter.prevent="openInfo(result.offering, 'offering', $event)"
            @keydown.space.prevent="openInfo(result.offering, 'offering', $event)"
          >
            <div class="slot-actions">
              <button type="button" class="slot-action slot-action--reroll" :disabled="rolling" :aria-label="rerollLabel('offering')" :title="rerollLabel('offering')" @click="rerollFrom('offering')">
                <span class="reroll-icon" aria-hidden="true"></span>
              </button>
              <button type="button" :class="['slot-action', 'slot-action--lock', { 'is-locked': locks.offering }]" :aria-label="lockLabel('offering')" :title="lockLabel('offering')" @click="toggleLock('offering')">
                <span class="lock-icon" aria-hidden="true"></span>
              </button>
            </div>
            <GameIcon :src="result.offering.icon" :alt="entryName(result.offering, 'offering')" :rarity="result.offering.rarity" kind="offering" />
            <div class="equipment-card__copy"><span class="equipment-card__type">{{ $t('forge.roulette.offering') }}</span><strong>{{ entryName(result.offering, 'offering') }}</strong><small>{{ $t('forge.roulette.chaosAffected') }}</small><div v-if="result.offering.correlation" class="fit-summary"><em class="fit-badge">{{ fitTier(result.offering.correlation) }} · {{ result.offering.correlation.score }}</em><small>{{ correlationReason(result.offering.correlation) }}</small></div></div>
            <span :class="['rarity-dot', rarityClass(result.offering.rarity)]">{{ rarityName(result.offering.rarity) }}</span>
          </article>
        </div>

        <footer class="analysis-strip">
          <div class="analysis-strip__signal">
            <span class="signal-pulse"></span>
            <div><small>{{ $t('forge.roulette.analysis') }}</small><strong>{{ profileSummary }}</strong></div>
          </div>
          <div class="analysis-strip__stats">
            <span><small>{{ $t('forge.roulette.buildFit') }}</small><strong>{{ result.correlation.score }}</strong></span>
            <span><small>{{ $t('forge.data.perks') }}</small><strong>{{ metrics.perks }}</strong></span>
            <span><small>{{ $t('forge.data.addons') }}</small><strong>{{ metrics.addons }}</strong></span>
            <span><small>{{ $t('forge.data.characters') }}</small><strong>{{ metrics.characters }}</strong></span>
          </div>
          <div class="analysis-strip__actions">
            <button type="button" @click="unlockAll">{{ $t('forge.roulette.unlockAll') }}</button>
            <button type="button" class="is-primary" @click="copyBuild">{{ copyStatus || $t('forge.roulette.copy') }}</button>
          </div>
        </footer>
      </section>
    </transition>
    <LoadoutInfoModal
      v-if="activeInfo"
      :entry="activeInfo.entry"
      :type="activeInfo.type"
      :side="side"
      @close="closeInfo"
    />
  </div>
</template>

<script>
import GameIcon from '@/components/GameIcon.vue'
import PerkIcon from '@/components/PerkIcon.vue'
import { createSurvivorCatalog } from '@/data/survivorPerks'
import { createKillerCatalog, getWikiMetrics, rollCompleteLoadout } from '@/lib/loadoutEngine'
import { equipmentScenarioLabel, equipmentStrategyText } from '@/lib/equipmentPackageEngine'
import { localizedCharacter, localizedEntryName } from '@/lib/loadoutLocalization'

export default {
  name: 'UnifiedRoulette',
  components: { GameIcon, PerkIcon, LoadoutInfoModal: () => import('@/components/LoadoutInfoModal.vue') },
  props: {
    perksKHD: { type: Object, required: true },
    perksSHD: { type: Object, required: true }
  },
  data () {
    return {
      side: this.$route.query.side === 'killer' ? 'killer' : 'survivor',
      chaos: [0, 25, 50, 75, 100].indexOf(Number(this.$route.query.chaos)) !== -1 ? Number(this.$route.query.chaos) : 25,
      chaosLevels: [0, 25, 50, 75, 100],
      result: null,
      rolling: false,
      rollingSlot: '',
      locks: {},
      copyStatus: '',
      activeInfo: null,
      survivorCatalog: [],
      killerCatalog: [],
      metrics: getWikiMetrics()
    }
  },
  computed: {
    chaosLabel () {
      if (this.chaos === 0) return this.$t('forge.chaos.curated')
      if (this.chaos <= 25) return this.$t('forge.chaos.coherent')
      if (this.chaos <= 50) return this.$t('forge.chaos.varied')
      if (this.chaos <= 75) return this.$t('forge.chaos.unstable')
      return this.$t('forge.chaos.void')
    },
    localizedCharacterDetails () {
      if (!this.result) return { displayName: '', realName: '', power: '' }
      return localizedCharacter(this.result.character, this.side, this.$i18n.locale)
    },
    characterName () {
      return this.localizedCharacterDetails.displayName
    },
    characterRealName () {
      return this.localizedCharacterDetails.realName
    },
    characterPower () {
      return this.localizedCharacterDetails.power
    },
    characterInitials () {
      return this.characterName.split(/\s+/).filter(word => word.toLowerCase() !== 'the').slice(0, 2).map(word => word.charAt(0)).join('').toUpperCase()
    },
    profileLabel () {
      return this.profileText('label')
    },
    profileSummary () {
      return this.profileText('summary')
    },
    planSecondaryLabel () {
      if (!this.result || !this.result.plan || !this.result.plan.secondary) return ''
      return this.planProfileText(this.result.plan.secondary, 'label')
    },
    planCoverageLabel () {
      if (!this.result || !this.result.plan) return ''
      return this.$t('forge.roulette.planCoverage', {
        count: this.result.plan.coverage,
        total: this.result.plan.coverageTotal
      })
    },
    slotOrder () {
      const perks = ['perk-0', 'perk-1', 'perk-2', 'perk-3']
      return this.side === 'survivor'
        ? ['character'].concat(perks, ['item', 'addon-0', 'addon-1', 'offering'])
        : ['character'].concat(perks, ['addon-0', 'addon-1', 'offering'])
    }
  },
  watch: {
    '$i18n.locale': function () {
      this.createCatalogs()
      this.performRoll()
    }
  },
  created () {
    this.createCatalogs()
    this.performRoll()
  },
  methods: {
    entryName (entry, type) {
      return localizedEntryName(entry, type, this.$i18n.locale)
    },
    infoLabel (entry, type) {
      const action = String(this.$i18n.locale || '').startsWith('fr') ? 'Voir la description officielle de' : 'View official description for'
      const name = type === 'perk' ? this.perkName(entry) : this.entryName(entry, type)
      return `${action} ${name}`
    },
    openInfo (entry, type, event) {
      if (!entry) return
      if (event && event.target && event.target.closest && event.target.closest('.slot-actions')) return
      this.activeInfo = { entry, type }
    },
    closeInfo () {
      this.activeInfo = null
    },
    categoryName (entry) {
      const key = `forge.categories.${entry && entry.category}`
      return this.$te(key) ? this.$t(key) : (entry && entry.categoryLabel) || this.$t('forge.roulette.item')
    },
    rarityName (rarity) {
      const keys = { 1: 'common', 2: 'uncommon', 3: 'rare', 4: 'veryRare', 5: 'visceral', 8: 'event', 11: 'limited' }
      return this.$t(`forge.rarity.${keys[rarity] || 'special'}`)
    },
    fitTier (correlation) {
      return this.$t(`forge.correlation.tiers.${(correlation && correlation.tier) || 'neutral'}`)
    },
    correlationReason (correlation) {
      if (correlation && correlation.strategy) {
        return equipmentStrategyText(correlation.strategy, 'labels', this.$i18n.locale)
      }
      if (correlation && correlation.scenario) {
        return equipmentScenarioLabel(correlation.scenario, this.$i18n.locale)
      }
      if (correlation && correlation.pairPlan) {
        const key = `forge.correlation.plans.${correlation.pairPlan}`
        return this.$te(key) ? this.$t(key) : correlation.pairPlan.replace(/_/g, ' ')
      }
      if (correlation && Number.isFinite(correlation.coverage) && correlation.coverageTotal) {
        return this.$t('forge.correlation.coverage', {
          count: correlation.coverage,
          total: correlation.coverageTotal
        })
      }
      const factors = (correlation && correlation.breakdown) || []
      const strongest = factors.find(entry => entry.id !== 'baseline') || factors[0]
      return strongest ? this.$t(`forge.correlation.factors.${strongest.id}`) : this.$t('forge.correlation.factors.neutralUtility')
    },
    profileText (field) {
      if (!this.result || !this.result.profile) return ''
      const key = `forge.profiles.${this.result.profile.id}.${field}`
      return this.$te(key) ? this.$t(key) : this.result.profile[field]
    },
    planProfileText (planId, field) {
      const key = `forge.profiles.${planId}.${field}`
      return this.$te(key) ? this.$t(key) : String(planId || '').replace(/_/g, ' ')
    },
    createCatalogs () {
      const survivorFrames = Object.keys(this.perksSHD.frames).map((name, index) => ({ index, name }))
      this.survivorCatalog = createSurvivorCatalog(survivorFrames)
      this.killerCatalog = createKillerCatalog(Object.keys(this.perksKHD.frames), frameName => this.$t(`perks.killer.${frameName}`))
    },
    performRoll (locks, rerollKey) {
      this.result = rollCompleteLoadout({
        side: this.side,
        chaos: this.chaos,
        survivorCatalog: this.survivorCatalog,
        killerCatalog: this.killerCatalog,
        previous: this.result,
        locks: locks || this.locks,
        rerollKey: rerollKey || null
      })
      this.copyStatus = ''
    },
    roll () {
      if (this.rolling) return
      this.rolling = true
      this.rollingSlot = 'all'
      window.setTimeout(() => {
        this.performRoll()
        this.rolling = false
        this.rollingSlot = ''
      }, 420)
    },
    rerollFrom (key) {
      if (this.rolling || !this.result) return
      const targetIndex = this.slotOrder.indexOf(key)
      if (targetIndex === -1) return

      const cascadeLocks = this.slotOrder.reduce((nextLocks, slot, index) => {
        if (index < targetIndex) nextLocks[slot] = true
        return nextLocks
      }, {})
      this.locks = this.slotOrder.reduce((nextLocks, slot, index) => {
        if (index < targetIndex && this.locks[slot]) nextLocks[slot] = true
        return nextLocks
      }, {})
      this.rolling = true
      this.rollingSlot = key
      window.setTimeout(() => {
        this.performRoll(cascadeLocks, key)
        this.rolling = false
        this.rollingSlot = ''
      }, 360)
    },
    setSide (side) {
      if (side === this.side) return
      this.side = side
      this.locks = {}
      this.rollingSlot = ''
      this.result = null
      this.syncQuery()
      this.performRoll()
    },
    syncQuery () {
      const query = Object.assign({}, this.$route.query, { side: this.side, chaos: String(this.chaos) })
      this.$router.replace({ path: this.$route.path, query }).catch(() => {})
    },
    toggleLock (key) {
      this.$set(this.locks, key, !this.locks[key])
    },
    unlockAll () {
      this.locks = {}
    },
    lockLabel (key) {
      return `${this.locks[key] ? this.$t('forge.roulette.unlock') : this.$t('forge.roulette.lock')} ${this.slotName(key)}`
    },
    rerollLabel (key) {
      return this.$t('forge.roulette.rerollFrom', { slot: this.slotName(key) })
    },
    slotName (key) {
      if (key === 'character') return this.$t('forge.roulette.character')
      if (key === 'item') return this.result && this.result.item ? this.categoryName(this.result.item) : this.$t('forge.roulette.item')
      if (key === 'offering') return this.$t('forge.roulette.offering')
      if (key.indexOf('perk-') === 0) return `${this.$t('forge.roulette.perks')} ${Number(key.split('-')[1]) + 1}`
      if (key.indexOf('addon-') === 0) return `${this.$t('forge.roulette.addon')} ${Number(key.split('-')[1]) + 1}`
      return key
    },
    perkSlotLabel (index) {
      return [
        this.$t('forge.roulette.slotAnchor'),
        this.$t('forge.roulette.slotSynergy'),
        this.$t('forge.roulette.slotSupport'),
        this.$t('forge.roulette.slotFlex')
      ][index]
    },
    isCascadeSlot (key) {
      if (!this.rolling) return false
      if (this.rollingSlot === 'all') return true
      const start = this.slotOrder.indexOf(this.rollingSlot)
      const current = this.slotOrder.indexOf(key)
      return start !== -1 && current >= start
    },
    perkName (perk) {
      return localizedEntryName(perk, 'perk', this.$i18n.locale)
    },
    perkRole (perk) {
      const roles = perk.roles || perk.tags || []
      return roles.length ? roles.slice(0, 2).join(' · ').replace(/_/g, ' ') : this.$t('forge.roulette.adaptiveRole')
    },
    rarityClass (rarity) {
      if (rarity === 11) return 'rarity-dot--limited'
      if (rarity === 8) return 'rarity-dot--event'
      if (rarity === 5) return 'rarity-dot--visceral'
      if (rarity === 4) return 'rarity-dot--very-rare'
      if (rarity === 3) return 'rarity-dot--rare'
      if (rarity === 2) return 'rarity-dot--uncommon'
      return 'rarity-dot--common'
    },
    rarityCardClass (rarity) {
      return this.rarityClass(rarity).replace('rarity-dot', 'rarity-card')
    },
    buildText () {
      const perkLine = this.result.perks.map(perk => this.perkName(perk)).join(' / ')
      const equipment = [this.result.item && this.entryName(this.result.item, 'item')]
        .concat(this.result.addons.map(addon => this.entryName(addon, 'addon')))
        .concat(this.result.offering ? [this.entryName(this.result.offering, 'offering')] : [])
        .filter(Boolean)
        .join(' / ')
      return `${this.characterName}\n${perkLine}\n${equipment}\nChaos ${this.chaos}% · Coherence ${this.result.coherence}/100\n${window.location.href}`
    },
    copyBuild () {
      const text = this.buildText()
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.copyStatus = this.$t('forge.roulette.copied')
        })
        return
      }
      const area = document.createElement('textarea')
      area.value = text
      document.body.appendChild(area)
      area.select()
      document.execCommand('copy')
      document.body.removeChild(area)
      this.copyStatus = this.$t('forge.roulette.copied')
    }
  }
}
</script>

<style lang="scss" scoped>
.roulette-lab { --cyan: #68dcff; --coral: #ff7f6d; color: #f4f3ed; }
.loadout-card--inspectable { cursor: pointer; }
.loadout-card--inspectable:focus-visible { outline: 2px solid var(--cyan); outline-offset: 3px; }
.loadout-card--inspectable:hover { border-color: rgba(104,220,255,.28); }
.eyebrow { color: var(--cyan); font-size: 11px; font-weight: 900; letter-spacing: .17em; text-transform: uppercase; }
.lab-hero { min-height: 228px; padding: 32px 38px; border: 1px solid rgba(115, 214, 244, .18); border-radius: 28px; background: radial-gradient(circle at 82% 28%, rgba(105, 220, 255, .14), transparent 27%), linear-gradient(135deg, rgba(20, 28, 34, .96), rgba(7, 10, 13, .92)); box-shadow: 0 28px 80px rgba(0, 0, 0, .34); display: flex; align-items: flex-end; justify-content: space-between; gap: 28px; overflow: hidden; position: relative; }
.lab-hero::before { content: 'RANDOM / REASON'; position: absolute; top: 28px; right: -12px; color: rgba(255,255,255,.035); font-size: clamp(54px, 9vw, 130px); font-weight: 950; letter-spacing: -.07em; white-space: nowrap; }
.lab-hero__copy { max-width: 720px; position: relative; z-index: 1; }
.lab-hero h1 { max-width: 690px; margin: 10px 0 15px; font-size: clamp(42px, 7vw, 78px); font-weight: 950; letter-spacing: -.065em; line-height: .88; }
.lab-hero p { max-width: 640px; margin: 0; color: rgba(238, 244, 245, .65); font-size: 16px; line-height: 1.65; }
.lab-hero__status { min-width: 158px; padding: 18px; border: 1px solid rgba(255,255,255,.1); border-radius: 18px; background: rgba(2, 6, 8, .5); backdrop-filter: blur(16px); position: relative; z-index: 1; }
.lab-hero__status span, .lab-hero__status small { display: block; color: rgba(255,255,255,.5); font-size: 10px; letter-spacing: .1em; text-transform: uppercase; }
.lab-hero__status strong { display: block; margin: 6px 0 3px; color: var(--coral); font-size: 38px; letter-spacing: -.05em; }
.roulette-console { margin: 14px 0; padding: 14px; border: 1px solid rgba(255,255,255,.08); border-radius: 24px; background: rgba(10, 14, 17, .88); box-shadow: 0 18px 44px rgba(0,0,0,.22); display: grid; grid-template-columns: minmax(250px, .8fr) minmax(340px, 1.35fr) minmax(220px, .65fr); gap: 14px; }
.side-switch { padding: 5px; border-radius: 16px; background: #070a0c; display: grid; grid-template-columns: 1fr 1fr; gap: 5px; }
.side-switch button { min-height: 76px; padding: 10px; border: 1px solid transparent; border-radius: 12px; background: transparent; color: rgba(255,255,255,.48); cursor: pointer; display: flex; align-items: center; gap: 9px; text-align: left; transition: .2s ease; }
.side-switch button:hover { color: rgba(255,255,255,.78); }
.side-switch button.is-active { border-color: rgba(104,220,255,.3); background: linear-gradient(145deg, rgba(104,220,255,.16), rgba(255,127,109,.05)); color: #fff; box-shadow: inset 0 0 24px rgba(104,220,255,.04); }
.side-switch__emblem { width: 44px; height: 48px; border: 1px solid rgba(255,255,255,.12); border-radius: 12px 4px 12px 4px; background: linear-gradient(145deg, rgba(255,255,255,.08), rgba(255,255,255,.015)); box-shadow: inset 0 0 20px rgba(0,0,0,.28); display: flex; align-items: center; justify-content: center; gap: 3px; flex: 0 0 auto; overflow: hidden; position: relative; transform: skewX(-4deg); }
.side-switch__emblem::after { content: ''; width: 28px; height: 1px; background: currentColor; box-shadow: 0 0 13px currentColor; opacity: .36; bottom: 7px; position: absolute; }
.side-switch__emblem i { width: 4px; border-radius: 1px; background: currentColor; box-shadow: 0 0 10px color-mix(in srgb, currentColor 48%, transparent); display: block; transform: skewX(-9deg); }
.side-switch__emblem--survivor { color: #68dcff; }
.side-switch__emblem--survivor i { height: 24px; }
.side-switch__emblem--survivor i:nth-child(2), .side-switch__emblem--survivor i:nth-child(4) { height: 31px; }
.side-switch__emblem--killer { color: #ff7f6d; gap: 5px; }
.side-switch__emblem--killer i { width: 5px; height: 31px; border-radius: 4px 0; transform: skewX(-16deg) rotate(8deg); }
.side-switch__emblem--killer i:nth-child(2) { height: 25px; transform: translateY(4px) skewX(-16deg) rotate(8deg); }
.side-switch button:first-child.is-active { border-color: rgba(104,220,255,.36); }
.side-switch button:last-child.is-active { border-color: rgba(255,127,109,.36); background: linear-gradient(145deg, rgba(255,127,109,.14), rgba(104,220,255,.04)); }
.side-switch button > span:last-child { font-size: 12px; font-weight: 900; letter-spacing: .05em; text-transform: uppercase; }
.side-switch small { display: block; margin-bottom: 3px; font-size: 8px; opacity: .55; }
.chaos-module { padding: 11px 14px; }
.chaos-module__header { display: flex; justify-content: space-between; align-items: center; }
.chaos-module__header strong { display: block; font-size: 16px; }
.chaos-module output { color: var(--coral); font-size: 26px; font-weight: 950; }
.chaos-module input { width: 100%; margin: 9px 0 3px; accent-color: var(--coral); cursor: pointer; }
.chaos-module__ticks { color: rgba(255,255,255,.28); font-size: 8px; display: flex; justify-content: space-between; }
.chaos-module p { margin: 7px 0 0; color: rgba(255,255,255,.43); font-size: 10px; }
.roll-button { min-height: 76px; border: 0; border-radius: 16px; background: #f2eee4; color: #101317; cursor: pointer; display: flex; justify-content: center; align-items: center; gap: 14px; font-size: 15px; font-weight: 950; letter-spacing: -.01em; text-align: left; transition: transform .18s ease, background .18s ease, box-shadow .18s ease; }
.roll-button:hover { background: #fff; box-shadow: 0 10px 30px rgba(242,238,228,.12); transform: translateY(-2px); }
.roll-button:disabled { cursor: wait; opacity: .72; }
.roll-button small { display: block; margin-bottom: 2px; color: #697078; font-size: 8px; letter-spacing: .12em; text-transform: uppercase; }
.roll-button__spark { width: 18px; height: 18px; background: #ff625b; box-shadow: 0 0 18px rgba(255,98,91,.32); display: inline-block; transform: rotate(45deg); }
.loadout-shell { padding: 20px; border: 1px solid rgba(255,255,255,.1); border-radius: 28px; background: linear-gradient(180deg, rgba(16,21,25,.97), rgba(7,10,12,.98)); box-shadow: 0 32px 90px rgba(0,0,0,.4); overflow: hidden; }
.loadout-shell__header { margin-bottom: 17px; padding: 4px 6px; display: flex; align-items: flex-end; justify-content: space-between; }
.loadout-shell__header h2 { margin: 4px 0 0; font-size: 24px; letter-spacing: -.03em; }
.build-plan-meta { margin-top: 7px; display: flex; align-items: center; gap: 8px; color: rgba(255,255,255,.52); font-size: 9px; }
.build-plan-meta span { color: var(--cyan); font-weight: 850; letter-spacing: .08em; text-transform: uppercase; }
.build-plan-meta strong { color: rgba(255,255,255,.72); font-weight: 700; }
.build-plan-meta em { padding: 3px 6px; border: 1px solid rgba(130,232,186,.22); border-radius: 999px; color: #82e8ba; font-style: normal; font-weight: 800; }
.loadout-score { display: flex; align-items: baseline; gap: 4px; }
.loadout-score span { margin-right: 8px; color: rgba(255,255,255,.45); font-size: 9px; letter-spacing: .12em; text-transform: uppercase; }
.loadout-score strong { color: #82e8ba; font-size: 34px; line-height: 1; }
.loadout-score small { color: rgba(255,255,255,.34); }
.loadout-grid { display: grid; grid-template-columns: minmax(290px, .76fr) minmax(0, 2.24fr); gap: 12px; }
.loadout-card { border: 1px solid rgba(255,255,255,.09); border-radius: 20px; background: linear-gradient(145deg, rgba(255,255,255,.058), rgba(255,255,255,.018)); position: relative; transition: border-color .2s ease, box-shadow .2s ease, transform .2s ease; }
.loadout-card:hover { border-color: rgba(255,255,255,.17); }
.slot-actions { display: flex; gap: 5px; opacity: .76; position: absolute; top: 10px; right: 10px; z-index: 5; transition: opacity .18s ease; }
.loadout-card:hover .slot-actions, .slot-actions:focus-within { opacity: 1; }
.slot-action { width: 29px; height: 29px; padding: 0; border: 1px solid rgba(255,255,255,.13); border-radius: 9px; background: rgba(5,7,9,.82); color: rgba(255,255,255,.48); cursor: pointer; display: grid; place-items: center; transition: color .16s ease, border-color .16s ease, background .16s ease, transform .16s ease; }
.slot-action:hover { border-color: rgba(104,220,255,.48); background: rgba(18,31,38,.96); color: var(--cyan); transform: translateY(-1px); }
.slot-action:focus-visible { outline: 2px solid var(--cyan); outline-offset: 2px; }
.slot-action:disabled { cursor: wait; opacity: .45; }
.slot-action--lock.is-locked { border-color: rgba(255,127,109,.62); background: rgba(74,27,24,.72); color: var(--coral); }
.reroll-icon { width: 13px; height: 13px; border: 2px solid currentColor; border-right-color: transparent; border-radius: 50%; position: relative; }
.reroll-icon::after { content: ''; width: 0; height: 0; border-top: 3px solid transparent; border-bottom: 3px solid transparent; border-left: 4px solid currentColor; position: absolute; right: -4px; top: -2px; transform: rotate(-28deg); }
.lock-icon { width: 11px; height: 9px; border: 1.5px solid currentColor; border-radius: 2px; position: relative; transform: translateY(2px); }
.lock-icon::before { content: ''; width: 7px; height: 7px; border: 1.5px solid currentColor; border-bottom: 0; border-radius: 7px 7px 0 0; position: absolute; left: 50%; top: -7px; transform: translateX(-50%); }
.slot-action--lock:not(.is-locked) .lock-icon::before { left: 35%; transform: translateX(-50%) rotate(-28deg); transform-origin: left bottom; }
.is-cascade-active { border-color: rgba(104,220,255,.46) !important; box-shadow: 0 0 0 1px rgba(104,220,255,.07), 0 12px 36px rgba(0,0,0,.25); transform: translateY(-3px); }
.is-cascade-active::before { content: ''; inset: 0; border-radius: inherit; background: linear-gradient(105deg, transparent 18%, rgba(104,220,255,.16) 48%, transparent 76%); pointer-events: none; position: absolute; z-index: 4; animation: cascade-scan .72s ease both; }
@keyframes cascade-scan { from { opacity: 0; transform: translateX(-45%); } 35% { opacity: 1; } to { opacity: 0; transform: translateX(45%); } }
.character-card { min-height: 318px; padding: 16px; display: grid; grid-template-columns: minmax(122px, .82fr) minmax(136px, 1.18fr); align-items: stretch; gap: 14px; overflow: hidden; }
.character-card__portrait { min-height: 284px; border: 1px solid rgba(255,255,255,.05); border-radius: 15px; background: radial-gradient(circle at 50% 44%, color-mix(in srgb, var(--profile-accent) 38%, transparent), transparent 57%), repeating-linear-gradient(105deg, transparent 0 14px, rgba(255,255,255,.025) 15px 16px); display: grid; place-items: center; overflow: hidden; position: relative; }
.character-card__portrait::after { content: ''; inset: 13px; border: 1px solid color-mix(in srgb, var(--profile-accent) 46%, transparent); border-radius: 11px 3px 11px 3px; box-shadow: inset 0 0 34px color-mix(in srgb, var(--profile-accent) 10%, transparent), 0 0 34px color-mix(in srgb, var(--profile-accent) 12%, transparent); position: absolute; z-index: 0; }
.character-card__portrait img { width: 100%; height: 100%; padding: 5px 0; box-sizing: border-box; object-fit: contain; object-position: center; filter: drop-shadow(0 12px 18px rgba(0,0,0,.55)); position: relative; z-index: 1; }
.character-card__portrait span { color: #fff; font-size: 38px; font-weight: 950; letter-spacing: -.08em; z-index: 1; }
.character-card__portrait small { bottom: 8px; padding: 3px 7px; border-radius: 999px; background: rgba(4,7,9,.76); color: rgba(255,255,255,.55); font-size: 8px; letter-spacing: .22em; position: absolute; z-index: 2; }
.character-card__copy { min-width: 0; padding: 46px 3px 5px; align-self: end; position: relative; z-index: 1; }
.character-card__copy > span { color: rgba(255,255,255,.38); font-size: 10px; letter-spacing: .16em; text-transform: uppercase; }
.character-card h3 { display: -webkit-box; margin: 4px 0; font-size: clamp(21px, 1.7vw, 28px); letter-spacing: -.035em; line-height: 1.04; overflow: hidden; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.character-card p { margin: 0; color: rgba(255,255,255,.48); font-size: 12px; }
.character-card strong { display: block; margin-top: 9px; color: var(--profile-accent, #68dcff); font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.perk-section { min-width: 0; }
.section-label { min-height: 51px; padding: 0 5px; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.section-label > div { min-width: 0; display: flex; align-items: center; gap: 13px; }
.section-label span { font-size: 11px; font-weight: 900; letter-spacing: .15em; text-transform: uppercase; white-space: nowrap; }
.section-label > small { color: #ff927f; font-size: 10px; white-space: nowrap; }
.cascade-hint { color: rgba(255,255,255,.48); font-size: 9px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cascade-hint i { width: 22px; height: 1px; margin-right: 7px; background: linear-gradient(90deg, var(--cyan), transparent); display: inline-block; vertical-align: middle; }
.perk-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 9px; position: relative; }
.perk-tile { min-width: 0; min-height: 316px; padding: 15px; background: linear-gradient(155deg, #1a2024, #111518 70%); overflow: visible; z-index: 1; }
.perk-tile:not(:last-child)::after { content: ''; width: 9px; height: 1px; background: linear-gradient(90deg, rgba(104,220,255,.72), rgba(104,220,255,.16)); position: absolute; top: 52%; right: -10px; }
.perk-tile__role { padding: 4px 7px; border: 1px solid rgba(104,220,255,.18); border-radius: 999px; background: rgba(104,220,255,.06); color: rgba(104,220,255,.82); font-size: 10px; font-weight: 900; letter-spacing: .1em; position: absolute; top: 11px; left: 11px; text-transform: uppercase; z-index: 2; }
.perk-tile:first-child { border-color: rgba(255,194,87,.3); background: linear-gradient(155deg, rgba(255,194,87,.075), #15191c 72%); }
.perk-tile__role--central { border-color: rgba(255,194,87,.58); background: rgba(255,194,87,.13); color: #ffd16f; box-shadow: 0 0 20px rgba(255,194,87,.12); }
.perk-tile__visual { height: 210px; display: grid; place-items: center; overflow: hidden; position: relative; }
.perk-tile__visual > div { transform: scale(1.02); transform-origin: center; }
.perk-tile__visual > span { right: 1px; bottom: -2px; color: rgba(255,255,255,.09); font-size: 38px; font-weight: 950; position: absolute; }
.perk-tile h3 { min-height: 42px; margin: 5px 0; font-size: 16px; line-height: 1.25; position: relative; z-index: 1; }
.perk-tile p { margin-bottom: 0; color: rgba(255,255,255,.52); font-size: 11px; text-transform: capitalize; }
.equipment-row { margin-top: 12px; display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 9px; }
.equipment-card { --rarity: #5d4533; --rarity-bright: #ab713c; min-width: 0; min-height: 174px; padding: 14px; border-color: color-mix(in srgb, var(--rarity-bright) 34%, rgba(255,255,255,.08)); background: linear-gradient(105deg, color-mix(in srgb, var(--rarity) 27%, transparent), transparent 56%), linear-gradient(145deg, rgba(255,255,255,.05), rgba(255,255,255,.015)); display: grid; grid-template-columns: 92px minmax(0, 1fr); grid-template-rows: 1fr auto; align-items: center; gap: 7px 13px; overflow: hidden; }
.equipment-card::after { content: ''; width: 3px; height: 48%; border-radius: 99px; background: var(--rarity-bright); box-shadow: 0 0 20px color-mix(in srgb, var(--rarity-bright) 64%, transparent); left: 0; top: 26%; position: absolute; }
.equipment-card > .game-icon { grid-row: 1 / -1; align-self: center; }
.equipment-card__copy { min-width: 0; padding-top: 24px; align-self: center; position: relative; z-index: 1; }
.equipment-card__type { width: max-content; max-width: calc(100% - 42px); margin-bottom: 8px; padding: 4px 7px; border: 1px solid color-mix(in srgb, var(--rarity-bright) 46%, transparent); border-radius: 6px 2px 6px 2px; background: color-mix(in srgb, var(--rarity) 34%, rgba(4,7,9,.78)); color: color-mix(in srgb, var(--rarity-bright) 82%, white 18%); font-size: 10px; font-weight: 950; letter-spacing: .13em; line-height: 1; overflow: hidden; text-overflow: ellipsis; text-transform: uppercase; white-space: nowrap; }
.equipment-card--item .equipment-card__type, .equipment-card--power .equipment-card__type { border-color: rgba(104,220,255,.36); background: rgba(104,220,255,.08); color: #8be7ff; }
.equipment-card--addon .equipment-card__type { border-color: rgba(255,127,109,.3); }
.equipment-card--offering .equipment-card__type { border-color: rgba(255,193,79,.38); color: #ffd071; }
.equipment-card small { display: block; margin-top: 7px; color: rgba(255,255,255,.64); font-size: 10px; font-weight: 700; letter-spacing: .055em; line-height: 1.4; text-transform: uppercase; }
.fit-summary { margin-top: 8px; display: flex; flex-direction: column; align-items: flex-start; gap: 5px; }
.fit-summary > small { max-width: 210px; margin-top: 0; color: rgba(255,255,255,.58); font-size: 8px; line-height: 1.4; text-transform: none; }
.fit-summary .fit-badge { margin-top: 0; }
.fit-badge { width: max-content; max-width: 100%; margin-top: 7px; padding: 3px 6px; border: 1px solid rgba(130,232,186,.28); border-radius: 999px; background: rgba(130,232,186,.06); color: #82e8ba; display: block; font-size: 9px; font-style: normal; font-weight: 900; letter-spacing: .08em; text-transform: uppercase; }
.equipment-card strong { display: -webkit-box; min-height: 36px; padding-right: 5px; color: rgba(255,255,255,.96); font-size: 15px; letter-spacing: -.015em; line-height: 1.28; overflow: hidden; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.rarity-dot { width: max-content; grid-column: 2; align-self: end; max-width: 100%; padding: 4px 8px; border: 1px solid color-mix(in srgb, var(--rarity-bright) 48%, transparent); border-radius: 999px; background: color-mix(in srgb, var(--rarity) 34%, rgba(255,255,255,.04)); color: color-mix(in srgb, var(--rarity-bright) 86%, white 14%); font-size: 9px; font-weight: 900; letter-spacing: .06em; overflow: hidden; text-overflow: ellipsis; text-transform: uppercase; white-space: nowrap; }
.rarity-card--common { --rarity: #5d4533; --rarity-bright: #ab713c; }
.rarity-card--uncommon { --rarity: #0f5f14; --rarity-bright: #199b1e; }
.rarity-card--rare { --rarity: #174c6b; --rarity-bright: #2f9cd6; }
.rarity-card--very-rare { --rarity: #672d7f; --rarity-bright: #ac3ee3; }
.rarity-card--visceral { --rarity: #cf0b45; --rarity-bright: #ff0955; }
.rarity-card--event { --rarity: #8a5d00; --rarity-bright: #ffa800; }
.rarity-card--limited { --rarity: #74341f; --rarity-bright: #c2593a; }
.rarity-card--power { --rarity: #164f66; --rarity-bright: #68dcff; }
.rarity-dot--common { color: #c88c56; } .rarity-dot--uncommon { color: #58c85c; } .rarity-dot--rare { color: #63b9e8; } .rarity-dot--very-rare { color: #ca82ec; } .rarity-dot--visceral { color: #ff4b78; } .rarity-dot--event { color: #ffc14f; } .rarity-dot--limited { color: #df825e; } .rarity-dot--power { color: #79e3ff; }
.analysis-strip { margin-top: 12px; padding: 14px; border: 1px solid rgba(255,255,255,.035); border-radius: 18px; background: #080b0d; display: grid; grid-template-columns: minmax(0, 1.7fr) auto auto; align-items: center; gap: 22px; }
.analysis-strip__signal { min-width: 0; display: flex; align-items: center; gap: 12px; }
.signal-pulse { width: 9px; height: 9px; border-radius: 50%; background: #82e8ba; box-shadow: 0 0 18px #82e8ba; flex: 0 0 auto; }
.analysis-strip__signal small { display: block; color: rgba(255,255,255,.42); font-size: 10px; letter-spacing: .12em; text-transform: uppercase; }
.analysis-strip__signal strong { display: block; color: rgba(255,255,255,.74); font-size: 12px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.analysis-strip__stats { display: flex; gap: 16px; }
.analysis-strip__stats span { text-align: right; }
.analysis-strip__stats small { display: block; color: rgba(255,255,255,.38); font-size: 9px; text-transform: uppercase; }
.analysis-strip__stats strong { font-size: 14px; }
.analysis-strip__actions { display: flex; gap: 7px; }
.analysis-strip__actions button { min-height: 34px; padding: 0 12px; border: 1px solid rgba(255,255,255,.12); border-radius: 10px; background: transparent; color: rgba(255,255,255,.65); cursor: pointer; font-size: 10px; font-weight: 800; text-transform: uppercase; }
.analysis-strip__actions button:hover { border-color: rgba(255,255,255,.3); color: #fff; }
.analysis-strip__actions button.is-primary { border-color: #f2eee4; background: #f2eee4; color: #111; }
.loadout-swap-enter-active, .loadout-swap-leave-active { transition: opacity .22s ease, transform .22s ease; }
.loadout-swap-enter, .loadout-swap-leave-to { opacity: 0; transform: translateY(10px) scale(.99); }
@media (max-width: 1180px) { .loadout-grid { grid-template-columns: minmax(260px, .7fr) minmax(0, 2.3fr); } .perk-tile__visual > div { transform: scale(.92); } .equipment-card { grid-template-columns: 72px minmax(0, 1fr); } .equipment-card > .game-icon { transform: scale(.88); transform-origin: left center; } }
@media (max-width: 1040px) { .roulette-console { grid-template-columns: 1fr 1.3fr; } .roll-button { min-height: 68px; grid-column: 1 / -1; } .loadout-grid { grid-template-columns: 1fr; } .character-card { min-height: 280px; grid-template-columns: 210px 1fr; align-items: stretch; gap: 20px; } .character-card__portrait { min-height: 248px; } .character-card__copy { padding-top: 54px; } .perk-tile__visual > div { transform: scale(1); } .analysis-strip { grid-template-columns: 1fr auto; } .analysis-strip__stats { display: none; } }
@media (max-width: 760px) { .lab-hero { min-height: 290px; padding: 28px 22px; align-items: flex-end; } .lab-hero__status { display: none; } .roulette-console { grid-template-columns: 1fr; } .roll-button { grid-column: auto; min-height: 66px; } .perk-grid, .equipment-row { grid-template-columns: repeat(2, minmax(0, 1fr)); } .perk-tile::after { display: none; } .character-card { grid-template-columns: minmax(140px, .72fr) 1fr; } .character-card__portrait { min-height: 250px; } .character-card__copy { padding-top: 48px; } .cascade-hint { max-width: 300px; } .analysis-strip { grid-template-columns: 1fr; } .analysis-strip__actions { display: grid; grid-template-columns: 1fr 1fr; } }
@media (max-width: 520px) { .section-label { align-items: flex-start; flex-direction: column; padding: 6px 5px 10px; } .section-label > div { align-items: flex-start; flex-direction: column; gap: 6px; } .cascade-hint { white-space: normal; } }
@media (max-width: 430px) { .lab-hero h1 { font-size: 43px; } .loadout-shell { padding: 12px; border-radius: 20px; } .perk-grid { grid-template-columns: 1fr 1fr; } .perk-tile { min-height: 238px; padding: 10px; } .perk-tile__visual { height: 144px; } .perk-tile__visual > div { transform: scale(.65); } .perk-tile__role { max-width: 68px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; } .equipment-row { grid-template-columns: 1fr; } .character-card { display: flex; } .character-card__portrait { min-height: 300px; } .character-card__copy { padding-top: 12px; } .loadout-shell__header { align-items: flex-start; } .loadout-score span { display: none; } }
@media (prefers-reduced-motion: reduce) { .is-cascade-active::before { animation: none; } .is-cascade-active { transform: none; } }
</style>
